import { error as logError } from "../../../utils/console";
import { round } from "../../../utils/global";

/** Stored target speeds are in km/h; TWS rows are exported at whole-knot increments. */
const KPH_PER_KNOT = 1.852;
/** Fixed TWS range (knots) for the exported table. Cells outside a target's data are left blank. */
const MIN_KNOTS = 6;
const MAX_KNOTS = 25;

/** ARGB fills echoing the reference "m14 Perth" sheet. */
const TITLE_FILL = "FFC55A11"; // rust/orange title band
const TITLE_FONT = "FFFFFFFF";
const GROUP_FILL = "FFF2F2F2";
const HEADER_FILL = "FFEFEFEF";
const BS_FILL = "FFFFFF00"; // yellow boat-speed columns
const TWA_FILL = "FFF4CCCC"; // light red true-wind-angle columns
const KMH_FONT = "FFFF0000"; // red km/h wind-speed numbers
const BORDER_COLOR = "FF808080";

/** Excel worksheet names: max 31 chars, no \ / * ? : [ ] characters. */
function sanitizeSheetName(name: string): string {
  const cleaned = name.replace(/[\\/*?:[\]]/g, " ").trim();
  return (cleaned === "" ? "Targets" : cleaned).slice(0, 31);
}

function sanitizeFileName(name: string): string {
  const cleaned = name.replace(/[\\/:*?"<>|]/g, " ").replace(/\s+/g, " ").trim();
  return cleaned === "" ? "Targets" : cleaned;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Resolve a logical field ("tws", "bsp", "twa") to the actual key present on a row,
 * tolerating case and unit/suffix variants (e.g. `Tws_kph`, `BSP`, `twa_deg`).
 * Mirrors the resolver in TargetTable.tsx so both read the same stored shape.
 */
function resolveField(row: Record<string, any>, logical: string): string | null {
  const base = logical.toLowerCase();
  const keys = Object.keys(row);
  const normalize = (s: string) =>
    s.toLowerCase().replace(/[_\s]*(kph|kts|deg|perc)$/i, "").replace(/[_\s]/g, "");
  if (base in row) {
    return base;
  }
  const match = keys.find((k) => normalize(k) === base);
  return match ?? null;
}

interface KnotRow {
  knots: number;
  bsp: number;
  twa: number;
}

/** Convert a stored point-of-sail array (kph) into knot-keyed rows, sorted ascending. */
export function toKnotRows(collection: any[] | undefined): KnotRow[] {
  if (!collection || collection.length === 0) {
    return [];
  }
  const first = collection[0];
  const twsField = resolveField(first, "tws");
  const bspField = resolveField(first, "bsp");
  const twaField = resolveField(first, "twa");
  if (!twsField || !bspField || !twaField) {
    return [];
  }
  return collection
    .map((d) => ({
      knots: (+d[twsField] || 0) / KPH_PER_KNOT,
      bsp: +d[bspField] || 0,
      twa: +d[twaField] || 0,
    }))
    .filter((r) => Number.isFinite(r.knots) && r.knots > 0)
    .sort((a, b) => a.knots - b.knots);
}

/**
 * Linearly interpolate bsp (kph) and twa (deg) at an integer knot value.
 * TWS is clamped to the data's [min,max] knot coverage, so values beyond the ends
 * flat-hold the nearest data point (matching the targets pipeline's np.clip + np.interp
 * in scripts/gp50/2_targets.py). Returns null only when there is no data.
 */
export function interpolateAt(rows: KnotRow[], k: number): { bsp: number; twa: number } | null {
  if (rows.length === 0) {
    return null;
  }
  const kc = Math.min(Math.max(k, rows[0].knots), rows[rows.length - 1].knots);
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].knots === kc) {
      return { bsp: rows[i].bsp, twa: rows[i].twa };
    }
    if (rows[i].knots > kc) {
      const lo = rows[i - 1];
      const hi = rows[i];
      const t = (kc - lo.knots) / (hi.knots - lo.knots);
      return {
        bsp: lo.bsp + t * (hi.bsp - lo.bsp),
        twa: lo.twa + t * (hi.twa - lo.twa),
      };
    }
  }
  return null;
}

/**
 * Export a single target as a styled .xlsx download: TWS in knots + km/h, plus
 * Upwind and Downwind TWA/BS interpolated to 1-knot increments (6..25 kts).
 * BS is shown in km/h as stored; TWA in degrees.
 */
export async function exportTargetExcel(
  targetName: string,
  data: Record<string, any[]>
): Promise<{ success: boolean; message?: string }> {
  try {
    const upwind = toKnotRows(data?.["UPWIND"]);
    const downwind = toKnotRows(data?.["DOWNWIND"]);
    if (upwind.length === 0 && downwind.length === 0) {
      return { success: false, message: "No target data to export" };
    }

    const ExcelJS = await import("exceljs");
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(sanitizeSheetName(targetName), {
      views: [{ showGridLines: false }],
    });

    const TOTAL_COLS = 6; // TWS(kts, kmh), Upwind(TWA, BS), Downwind(TWA, BS)
    sheet.getColumn(1).width = 8;
    sheet.getColumn(2).width = 8;
    for (let c = 3; c <= TOTAL_COLS; c++) {
      sheet.getColumn(c).width = 9;
    }

    const thinBorder = {
      top: { style: "thin" as const, color: { argb: BORDER_COLOR } },
      left: { style: "thin" as const, color: { argb: BORDER_COLOR } },
      bottom: { style: "thin" as const, color: { argb: BORDER_COLOR } },
      right: { style: "thin" as const, color: { argb: BORDER_COLOR } },
    };
    const solidFill = (argb: string) => ({
      type: "pattern" as const,
      pattern: "solid" as const,
      fgColor: { argb },
    });
    const center = { horizontal: "center" as const, vertical: "middle" as const };
    /** Columns (1-based) carrying boat speed, filled yellow like the reference sheet. */
    const bsCols = new Set([4, 6]);
    /** Columns (1-based) carrying true wind angle, filled light red. */
    const twaCols = new Set([3, 5]);
    /** Fill color for a data/header column, or undefined for the default. */
    const colFill = (c: number) => (bsCols.has(c) ? BS_FILL : twaCols.has(c) ? TWA_FILL : undefined);

    // Row 1: title band (target name).
    sheet.mergeCells(1, 1, 1, TOTAL_COLS);
    const titleCell = sheet.getCell(1, 1);
    titleCell.value = targetName;
    titleCell.font = { bold: true, size: 14, color: { argb: TITLE_FONT } };
    titleCell.fill = solidFill(TITLE_FILL);
    titleCell.alignment = center;
    titleCell.border = thinBorder;
    sheet.getRow(1).height = 24;

    // Row 2: group headers — TWS | Upwind | Downwind (each spanning 2 columns).
    const groups: Array<[string, number, number]> = [
      ["TWS", 1, 2],
      ["Upwind", 3, 4],
      ["Downwind", 5, 6],
    ];
    for (const [label, from, to] of groups) {
      sheet.mergeCells(2, from, 2, to);
      const cell = sheet.getCell(2, from);
      cell.value = label;
      cell.font = { bold: true, size: 12 };
      cell.fill = solidFill(GROUP_FILL);
      cell.alignment = center;
    }
    for (let c = 1; c <= TOTAL_COLS; c++) {
      sheet.getCell(2, c).border = thinBorder;
    }
    sheet.getRow(2).height = 20;

    // Row 3: sub-headers.
    const subHeaders = ["Knts", "Kmh", "TWA", "BS", "TWA", "BS"];
    for (let c = 1; c <= TOTAL_COLS; c++) {
      const cell = sheet.getCell(3, c);
      cell.value = subHeaders[c - 1];
      cell.font = { bold: true, size: 10 };
      cell.fill = solidFill(colFill(c) ?? HEADER_FILL);
      cell.alignment = center;
      cell.border = thinBorder;
    }
    sheet.getRow(3).height = 18;

    // Data rows: whole knots MIN..MAX. Blank where a point-of-sail lacks coverage.
    let rowIndex = 4;
    for (let k = MIN_KNOTS; k <= MAX_KNOTS; k++) {
      const uw = interpolateAt(upwind, k);
      const dw = interpolateAt(downwind, k);
      const values: (number | null)[] = [
        k,
        round(k * KPH_PER_KNOT, 1),
        uw ? round(uw.twa, 0) : null,
        uw ? round(uw.bsp, 0) : null,
        dw ? round(dw.twa, 0) : null,
        dw ? round(dw.bsp, 0) : null,
      ];
      for (let c = 1; c <= TOTAL_COLS; c++) {
        const cell = sheet.getCell(rowIndex, c);
        const v = values[c - 1];
        if (v != null) {
          cell.value = v;
        }
        // Col 2 (Kmh wind speed) uses red font; all numbers bold and enlarged.
        cell.font = { size: 14, bold: true, ...(c === 2 ? { color: { argb: KMH_FONT } } : {}) };
        cell.alignment = center;
        cell.border = thinBorder;
        const fill = colFill(c);
        if (fill) {
          cell.fill = solidFill(fill);
        }
      }
      sheet.getRow(rowIndex).height = 20;
      rowIndex++;
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    downloadBlob(blob, `${sanitizeFileName(targetName)} - Targets.xlsx`);
    return { success: true };
  } catch (err) {
    logError("Targets: export target Excel failed", err);
    return {
      success: false,
      message: err instanceof Error ? err.message : "Failed to export Excel file",
    };
  }
}
